// ── Design decisions ──
// Paso 2 de la agencia: escritorio primero (1280–1440 px), a ANCHO COMPLETO.
//
// · Barra superior: ID de la encomienda, la acción primaria "Analizar lista"
//   (Alt+Shift+A, atajo visible al pasar el mouse y al enfocar; ningún atajo
//   pisa Ctrl+L / Ctrl+T / Ctrl+W), contadores (paquetes, cajas, artículos
//   por revisar) y totales en vivo (FOB, impuesto estimado vía customsCalc,
//   peso total de cajas).
// · Ya no hay riel derecho: las cajas son una SECCIÓN COLAPSABLE bajo la
//   barra superior, cerrada por defecto, con una tira resumen de una línea
//   (Cajas · N · X lbs · ⚠ incompletas · paquetes sin caja, los dos últimos en
//   ámbar y ocultos cuando valen cero) y el botón "Mostrar cajas ▾"
//   (Alt+Shift+C, atajo visible al pasar el mouse y al enfocar). Abierta, las
//   CajaCard van en una grilla a ancho completo (auto-fill, minmax(18rem, 1fr))
//   con el mismo autoguardado de siempre. Abierto/cerrado se recuerda por
//   visor en localStorage, envuelto en try/catch: sin storage igual funciona.
//   Colapsar es seguro porque el chip "Sin caja ▾" de cada paquete asigna una
//   caja sin abrir la sección. Una caja intacta (sin peso, dimensiones ni
//   paquetes) no cuenta como incompleta: es un marcador, no un olvido.
// · La columna de paquetes usa siempre todo el ancho; cada PaqueteBlock es un
//   contenedor CSS y sus filas se dimensionan contra la tarjeta (ver
//   AgencyEncomienda.css, acotado bajo la raíz `.agency-step2`).
// · Estado vacío cálido SOLO cuando la encomienda no tiene ninguna fila de
//   artículo (guardada o sin guardar), con Analizar lista como acción obvia y
//   "Agregar artículo a mano" como secundaria. Con una fila ya no se muestra.
// · El peso vive SOLO en CajaCard: acá se muestra el total de cajas, nunca
//   se captura. Nada de esto guarda por su cuenta: todo sube por callbacks al
//   controlador del wizard, que es el único dueño de las escrituras.
// · El listener global de teclado se registra sólo mientras el paso está
//   montado; ignora Alt+Shift+A cuando el drawer ya está abierto.
// · El drawer de análisis es un panel lateral (no modal); los modales quedan
//   reservados para confirmaciones destructivas.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, ScanLine } from 'lucide-react';
import AnalizarListaDrawer from './AnalizarListaDrawer';
import PaqueteBlock from './PaqueteBlock';
import CajaCard from './CajaCard';
import { subtotalItems, tierImpuesto } from './agencyMapper';
import './AgencyEncomienda.css';

export const ATAJO_ANALIZAR = 'Alt+Shift+A';
export const ATAJO_CAJAS = 'Alt+Shift+C';
export const CLAVE_CAJAS_ABIERTAS = 'agencyStep2.cajasAbiertas';

const esAtajo = (e, letra) => e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey && String(e.key).toLowerCase() === letra;

const tieneContenido = (it) => String(it.description || '').trim() !== '' || parseFloat(it.unitValue) > 0;

const cajaValida = (c) =>
  parseFloat(c.weight_lb) > 0 && String(c.dimensions || '').trim() !== '' && (c.packageIds || []).length > 0;
const cajaIntacta = (c) => !c.weight_lb && !String(c.dimensions || '').trim() && (c.packageIds || []).length === 0 && !c.dbBoxId;

const leerCajasAbiertas = () => {
  try {
    return window.localStorage.getItem(CLAVE_CAJAS_ABIERTAS) === '1';
  } catch (_) {
    return false;
  }
};

const guardarCajasAbiertas = (abiertas) => {
  try {
    window.localStorage.setItem(CLAVE_CAJAS_ABIERTAS, abiertas ? '1' : '0');
  } catch (_) {
    // sin storage (privado, bloqueado): la página funciona igual
  }
};

const AgencyStep2 = ({
  encomiendaPublicId,
  encomiendaDbId,
  tenantId,
  customerId,
  paquetes = [],
  cajas = [],
  destinatarios = [],
  customsConfig,
  defaultRecipient,
  api,
  notice = null,
  saveStates = {},
  onAddItem,
  onUpdateItem,
  onItemBlur,
  onRemoveItem,
  onMoveItem,
  onAddPackage,
  onDeletePackage,
  onUpdateRecipient,
  onAddCaja,
  onUpdateCaja,
  onDeleteCaja,
  onAssignPackageToCaja,
  onAplicarGrupo,
  onAplicado,
  onAnalyzeReceipt,
  analyzingPackageId,
}) => {
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [agregandoPaquete, setAgregandoPaquete] = useState(false);
  const [focoManual, setFocoManual] = useState(null); // { pkgId, tempId }
  const [cajasAbiertas, setCajasAbiertas] = useState(leerCajasAbiertas);
  const abiertoRef = useRef(false);
  abiertoRef.current = drawerAbierto;

  const cerrarDrawer = useCallback(() => setDrawerAbierto(false), []);

  const alternarCajas = useCallback(() => {
    setCajasAbiertas((v) => {
      guardarCajasAbiertas(!v);
      return !v;
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (esAtajo(e, 'a')) {
        if (abiertoRef.current) return;
        e.preventDefault();
        setDrawerAbierto(true);
      } else if (esAtajo(e, 'c')) {
        e.preventDefault();
        alternarCajas();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [alternarCajas]);

  const todosItems = useMemo(() => paquetes.flatMap((p) => p.items || []), [paquetes]);
  const conContenido = todosItems.filter(tieneContenido);
  const porRevisar = conContenido.filter((it) => it.pending_review).length;
  const fob = subtotalItems(conContenido);
  // Impuesto por paquete y recién después sumado — nunca sobre el total.
  const impuesto = paquetes.reduce(
    (s, p) => Math.round((s + tierImpuesto((p.items || []).filter(tieneContenido), customsConfig).total) * 100) / 100,
    0
  );
  const pesoCajas = cajas.reduce((s, c) => s + (parseFloat(c.weight_lb) || 0), 0);
  // Estado vacío sólo sin NINGUNA fila (guardada o sin guardar).
  const sinFilas = todosItems.length === 0;
  const incompletas = cajas.filter((c) => !cajaValida(c) && !cajaIntacta(c)).length;
  const sinCaja = paquetes.filter((p) => !p.cajaLocalId).length;

  const buscarCatalogo = useCallback(
    (descripcion) => (api && typeof api.buscarCatalogoFila === 'function' ? api.buscarCatalogoFila(descripcion, { limit: 5 }) : Promise.resolve([])),
    [api]
  );

  const agregarPaquete = async () => {
    if (!onAddPackage || agregandoPaquete) return;
    setAgregandoPaquete(true);
    try {
      await onAddPackage();
    } finally {
      setAgregandoPaquete(false);
    }
  };

  const agregarAMano = () => {
    const primero = paquetes[0];
    if (!primero) return;
    const vacia = (primero.items || []).find((it) => !tieneContenido(it));
    if (vacia) {
      setFocoManual({ pkgId: primero.id, tempId: vacia.tempId });
      return;
    }
    const tempId = onAddItem ? onAddItem(primero.id) : null;
    if (tempId) setFocoManual({ pkgId: primero.id, tempId });
  };

  const botonAnalizar = (
    <button
      type="button"
      onClick={() => setDrawerAbierto(true)}
      aria-label={`Analizar lista (${ATAJO_ANALIZAR})`}
      aria-keyshortcuts="Alt+Shift+A"
      title={`Analizar lista del cliente — ${ATAJO_ANALIZAR}`}
      className="btn btn-primary group focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
      style={{ minHeight: '44px', fontSize: '12px', justifyContent: 'center' }}
    >
      <ScanLine size={15} aria-hidden="true" /> Analizar lista
      <kbd
        className="ml-2 hidden rounded border px-1 font-mono text-[9px] opacity-80 group-hover:inline group-focus-visible:inline"
        style={{ borderColor: 'rgba(255,255,255,0.6)' }}
        aria-hidden="true"
      >
        {ATAJO_ANALIZAR}
      </kbd>
    </button>
  );

  return (
    <div className="agency-step2 space-y-4" data-testid="agency-step2">
      {/* Barra superior */}
      <div className="tile flex flex-wrap items-center gap-3" style={{ padding: '12px 16px' }}>
        <span className="badge badge-enc" style={{ fontSize: '11px' }}>
          {encomiendaPublicId || 'Encomienda'}
        </span>
        {botonAnalizar}
        <div className="flex flex-wrap items-center gap-2 ml-auto" aria-label="Contadores" role="group">
          <span className="badge badge-cas">{paquetes.length} paquetes</span>
          <span className="badge badge-cas">{cajas.length} cajas</span>
          <span className={`badge ${porRevisar > 0 ? 'badge-hold' : 'badge-done'}`}>{porRevisar} por revisar</span>
        </div>
        <div className="metric-strip w-full" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }} aria-label="Totales en vivo" role="group">
          <div className="metric-card" style={{ padding: '8px 12px' }}>
            <div className="metric-label">FOB total</div>
            <div className="metric-value" style={{ fontSize: '16px', color: 'var(--green)' }}>${fob.toFixed(2)}</div>
          </div>
          <div className="metric-card" style={{ padding: '8px 12px' }}>
            <div className="metric-label">Impuesto estimado</div>
            <div className="metric-value" style={{ fontSize: '16px', color: 'var(--amber)' }}>${impuesto.toFixed(2)}</div>
          </div>
          <div className="metric-card" style={{ padding: '8px 12px' }}>
            <div className="metric-label">Peso total de cajas</div>
            <div className="metric-value" style={{ fontSize: '16px' }}>
              {pesoCajas.toFixed(1)} <span className="metric-sub inline">lbs</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cajas: tira resumen + sección colapsable a ancho completo */}
      <section className="tile" aria-label="Cajas" style={{ padding: '8px 12px' }}>
        <div className="agency-cajas__resumen">
          <button
            type="button"
            onClick={alternarCajas}
            aria-expanded={cajasAbiertas}
            aria-controls="agency-cajas-panel"
            aria-keyshortcuts="Alt+Shift+C"
            title={`${cajasAbiertas ? 'Ocultar' : 'Mostrar'} las cajas — ${ATAJO_CAJAS}`}
            className="btn btn-secondary group focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
            style={{ minHeight: '36px' }}
          >
            {cajasAbiertas ? <ChevronUp size={13} aria-hidden="true" /> : <ChevronDown size={13} aria-hidden="true" />}
            {cajasAbiertas ? 'Ocultar cajas' : 'Mostrar cajas'}
            <kbd
              className="ml-2 hidden rounded border px-1 font-mono text-[9px] opacity-80 group-hover:inline group-focus-visible:inline"
              style={{ borderColor: 'var(--border)' }}
              aria-hidden="true"
            >
              {ATAJO_CAJAS}
            </kbd>
          </button>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }} data-testid="cajas-resumen">
            <span className="font-semibold" style={{ color: 'var(--text-main)' }}>Cajas</span>
            {' · '}{cajas.length}
            {' · '}{pesoCajas.toFixed(1)} lbs
          </span>
          {incompletas > 0 && (
            <span className="badge badge-hold" role="status">
              ⚠ {incompletas} incompleta{incompletas === 1 ? '' : 's'}
            </span>
          )}
          {sinCaja > 0 && (
            <span className="badge badge-hold" role="status">
              {sinCaja} paquete{sinCaja === 1 ? '' : 's'} sin caja
            </span>
          )}
        </div>

        {cajasAbiertas && (
          <div id="agency-cajas-panel">
            {cajas.length === 0 ? (
              <p className="empty-state" style={{ padding: '8px 0' }}>
                Todavía no hay cajas. Agregá la primera cuando tengas el paquete pesado y medido.
              </p>
            ) : (
              <div className="agency-cajas__grid">
                {cajas.map((c) => (
                  <CajaCard
                    key={c.localId}
                    caja={c}
                    paquetes={paquetes}
                    cajas={cajas}
                    onUpdateCaja={onUpdateCaja}
                    onDeleteCaja={onDeleteCaja}
                    canDelete={cajas.length > 1 || Boolean(c.dbBoxId)}
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => onAddCaja && onAddCaja()}
              aria-label="Agregar caja"
              className="btn btn-ghost mt-3 focus-visible:ring-2 focus-visible:ring-[var(--avianca-red)]"
              style={{ color: 'var(--avianca-red)', fontWeight: 'bold', minHeight: '44px' }}
            >
              <Plus size={13} aria-hidden="true" /> Agregar caja
            </button>
          </div>
        )}
      </section>

      {notice && (
        <p className="badge badge-hold w-full" role="status" style={{ whiteSpace: 'normal', padding: '8px 12px' }}>
          {notice}
        </p>
      )}

      {/* Paquetes: siempre a ancho completo */}
      <div className="agency-step2__col space-y-4">
        {sinFilas && (
          <div className="tile text-center space-y-3" role="status">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Todavía no hay artículos. Analizá la lista del cliente o agregá el primero a mano.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {botonAnalizar}
              <button
                type="button"
                onClick={agregarAMano}
                aria-label="Agregar artículo a mano"
                className="btn btn-secondary focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
                style={{ minHeight: '44px' }}
              >
                <Plus size={13} aria-hidden="true" /> Agregar artículo a mano
              </button>
            </div>
          </div>
        )}

        {paquetes.map((p) => (
          <PaqueteBlock
            key={p.id}
            paquete={p}
            paquetes={paquetes}
            cajas={cajas}
            customsConfig={customsConfig}
            api={api}
            encomiendaPublicId={encomiendaPublicId}
            saveStates={saveStates}
            canDelete={paquetes.length > 1}
            focusTempId={focoManual?.pkgId === p.id ? focoManual.tempId : null}
            onAddItem={onAddItem}
            onUpdateItem={onUpdateItem}
            onItemBlur={onItemBlur}
            onRemoveItem={onRemoveItem}
            onMoveItem={onMoveItem}
            onDeletePackage={onDeletePackage}
            onUpdateRecipient={onUpdateRecipient}
            onAssignPackageToCaja={onAssignPackageToCaja}
            onAnalyzeReceipt={onAnalyzeReceipt}
            buscarCatalogo={buscarCatalogo}
          />
        ))}

        <button
          type="button"
          onClick={agregarPaquete}
          disabled={agregandoPaquete || !encomiendaDbId}
          aria-label="Agregar paquete"
          className="btn btn-secondary w-full focus-visible:ring-2 focus-visible:ring-[var(--blue)]"
          style={{ borderStyle: 'dashed', minHeight: '44px', justifyContent: 'center' }}
        >
          <Plus size={14} aria-hidden="true" /> {agregandoPaquete ? 'Agregando…' : 'Agregar paquete'}
        </button>
      </div>

      <AnalizarListaDrawer
        open={drawerAbierto}
        onClose={cerrarDrawer}
        tenantId={tenantId}
        destinatarios={destinatarios}
        paquetes={paquetes}
        api={api}
        onAplicarGrupo={onAplicarGrupo}
        onAplicado={onAplicado}
      />
    </div>
  );
};

export default AgencyStep2;
